import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { ConversationDto } from '@/Domain/api/Conversation/dto/Conversation.dto';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { ExclamationMarkIcon } from '@/Domain/components/icons/ExclamationMarkIcon';

type ResolutionStatus =
  | 'confirmed_resolution'
  | 'assumed_resolution'
  | 'unresolved'
  | 'greetings_only';

type OutcomeConfig = {
  icon: React.ReactNode;
  iconClassName: string;
  labelKey: string;
};

const outcomeConfigMap: Record<ResolutionStatus, OutcomeConfig> = {
  confirmed_resolution: {
    icon: <Icon icon="mingcute:check-line" className="size-4 p-0.5" />,
    iconClassName: 'bg-[hsl(160_84%_39%)] text-white rounded-full',
    labelKey: 'conversation_details.unified_analytics.confirmed_resolution',
  },
  assumed_resolution: {
    icon: <Icon icon="mingcute:check-line" className="size-4 p-0.5" />,
    iconClassName: 'bg-[hsl(158_60%_62%)] text-white rounded-full',
    labelKey: 'conversation_details.unified_analytics.assumed_resolution',
  },
  unresolved: {
    icon: <ExclamationMarkIcon />,
    iconClassName: 'bg-red-100 text-red-500 rounded-full',
    labelKey: 'conversation_details.unified_analytics.unresolved',
  },
  greetings_only: {
    icon: <Icon icon="mingcute:chat-3-line" className="size-4 p-0.5" />,
    iconClassName: 'bg-muted text-foreground-subtle rounded-full',
    labelKey: 'conversation_details.unified_analytics.greetings_only',
  },
};

type Props = {
  conversation: ConversationDto;
};

export const UnifiedOutcome = ({ conversation }: Props) => {
  const { t } = useTranslation('inbox_v2');

  const unifiedAnalysis = conversation.unified_analysis;

  if (!unifiedAnalysis || !unifiedAnalysis.resolution_status) {
    return (
      <>
        <Separator />
        <Tooltip>
          <div className="flex flex-col gap-2">
            <section className="flex items-center gap-1 text-foreground-subtle">
              <p className="font-semibold text-foreground-subtle">
                {t('conversation_details.outcome.title')}
              </p>
              <TooltipTrigger>
                <Icon icon="mingcute:information-line" />
              </TooltipTrigger>
              <TooltipContent className="absolute left-8 -translate-y-10 -translate-x-1/2 w-[250px]">
                {t('tooltip.outcome_description')}
              </TooltipContent>
            </section>
            <p className="text-foreground-subtle loading-text">
              {t('conversation_details.unified_analytics.pending')}
            </p>
          </div>
        </Tooltip>
      </>
    );
  }

  const config = outcomeConfigMap[unifiedAnalysis.resolution_status];

  if (!config) {
    return null;
  }

  return (
    <>
      <Separator />
      <Tooltip>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between mb-2">
            <section className="flex items-center gap-1 text-foreground-subtle">
              <p className="font-semibold text-foreground-subtle">
                {t('conversation_details.outcome.title')}
              </p>
              <TooltipTrigger>
                <Icon icon="mingcute:information-line" />
              </TooltipTrigger>
              <TooltipContent className="absolute left-8 -translate-y-10 -translate-x-1/2 w-[250px]">
                {t('tooltip.outcome_description')}
              </TooltipContent>
            </section>
          </div>

          <div className="flex items-center gap-2">
            <div className={config.iconClassName}>{config.icon}</div>

            <p className="text-foreground-subtle">{t(config.labelKey)}</p>
          </div>
        </div>
      </Tooltip>
    </>
  );
};
